import { constant, get, has, noop } from 'lodash/fp';

import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';
import {
  CHALLENGE,
  COMPATIBLE_CHALLENGES,
  OPPONENT_ENTRY,
  USER_SIGN_OUT
} from '../constants/apiTypes';
import decodeEntry from '../normalizers/decodeEntry';
import {
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  FILTER_ENTRY_STATUS,
  RESET_CHALLENGE,
  RESET_OPPONENT_ENTRY,
  RESET_PERFORMANCE_DIFF,
  SET_PERFORMANCE_DIFF,
  SUBMIT_CHALLENGE_SUCCESS,
  UPDATE_CHALLENGE_SUCCESS,
  RESET_COMPATIBLE_CHALLENGES
} from '../constants/actionTypes';

const initialState = {
  requestedChallengeId: null,
  isLoadingOpponent: false,
  myEntry: {},
  opponentEntry: {},
  opponentScheduledPlayerCount: 0,
  restrictToUnique: false,
  selectionHash: null,
  statusFilters: {}
};

const apiRequestReducer = switchFunc(
  {
    [CHALLENGE]: (state, action) => ({
      requestedChallengeId: action.id
    }),
    [OPPONENT_ENTRY]: () => ({
      isLoadingOpponent: true
    })
  },
  constant(noop)
);

const apiSuccessReducer = switchFunc(
  {
    [CHALLENGE]: (state, action) => {
      // [MC] submit an entry on a multiEntry challenge. When it polls for updates we need to maintain the entry,
      // hence if the challenge is the same as before we must be polling.
      // this is a bit of a hack and ideally we would store the entry id in component state instead.
      if (state.requestedChallengeId === action.data.id) {
        const currentUserEntry = get('current_user_entry', action.data);
        return {
          myEntry: currentUserEntry
            ? decodeEntry(currentUserEntry, get('games', action.data))
            : state.myEntry,
          opponentEntry: has('opponent_entry', action.data)
            ? decodeEntry(get('opponent_entry', action.data), get('games', action.data))
            : state.opponentEntry,
          opponentScheduledPlayerCount: has('opponent_entry', action.data)
            ? (get('opponent_entry.selection.players', action.data) || []).filter(p => !p).length
            : state.opponentScheduledPlayerCount
        };
      }
      return {};
    },
    [COMPATIBLE_CHALLENGES]: (state, action) => ({
      myEntry: decodeEntry(
        {
          selection: get('lineup.selection', action.meta)
        },
        get('games', action.data)
      )
    }),
    [OPPONENT_ENTRY]: (state, action) => ({
      isLoadingOpponent: false,
      myEntry: decodeEntry(get('current_user_entry', action.data), get('games', action.data)),
      opponentEntry: decodeEntry(get('opponent_entry', action.data), get('games', action.data)),
      opponentScheduledPlayerCount: (
        get('opponent_entry.selection.players', action.data) || []
      ).filter(p => !p).length
    }),
    [USER_SIGN_OUT]: () => ({
      myEntry: {}
    })
  },
  constant(noop)
);

export default createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiRequestReducer(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccessReducer(action.api)(state, action),
  [FILTER_ENTRY_STATUS]: (state, action) => ({
    statusFilters: {
      ...state.statusFilters,
      [action.value]: !state.statusFilters[action.value]
    }
  }),
  [RESET_OPPONENT_ENTRY]: () => ({
    opponentEntry: {},
    opponentScheduledPlayerCount: 0
  }),
  [RESET_PERFORMANCE_DIFF]: () => ({
    restrictToUnique: initialState.restrictToUnique
  }),
  [SET_PERFORMANCE_DIFF]: (state, action) => ({
    restrictToUnique: !!action.value
  }),
  [RESET_CHALLENGE]: constant(initialState),
  [RESET_COMPATIBLE_CHALLENGES]: constant(initialState),
  [SUBMIT_CHALLENGE_SUCCESS]: (state, action) => ({
    lineupId: get('lineup_id', action.data),
    myEntry: decodeEntry(action.data),
    selectionHash: get('selection_hash', action.data)
  }),
  [UPDATE_CHALLENGE_SUCCESS]: (state, action) => ({
    myEntry: decodeEntry(action.data)
  })
});
